import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { User } from "../types";
import { getPotwProfile, getAnnualReport, getSamplingSchedule,
         getPendingEnforcement, logout } from "../api/client";

interface Props { user: User; onLogout: () => void; }

const ROLE_LABEL: Record<string, string> = {
  admin: "Administrator", coordinator: "Coordinator",
  finance: "Finance", iu: "Industrial User",
};

/**
 * Control Authority home — the program's main page. Presents the Control
 * Authority's identity (name + state designation/prefix from the POTW profile)
 * and, for program staff, a program-at-a-glance, then routes into the role
 * dashboard.
 */
export default function ControlAuthorityHome({ user, onLogout }: Props) {
  const [profile, setProfile] = useState<any | null>(null);
  const [stats, setStats] = useState<{ ius: number; inSnc: number;
    due: number; pendingEnf: number } | null>(null);

  const isStaff = user.role === "admin" || user.role === "coordinator";

  useEffect(() => {
    getPotwProfile().then(r => setProfile(r.data)).catch(() => {});
    if (!isStaff) return;
    const year = new Date().getFullYear();
    Promise.all([
      getAnnualReport(year).then(r => r.data).catch(() => null),
      getSamplingSchedule().then(r => r.data).catch(() => []),
      getPendingEnforcement().then(r => r.data).catch(() => []),
    ]).then(([annual, schedule, pending]) => {
      const due = (schedule as any[]).filter(
        r => r.status === "overdue" || r.status === "due_soon").length;
      setStats({
        ius: annual?.summary?.industrial_users ?? 0,
        inSnc: annual?.summary?.in_snc ?? 0,
        due,
        pendingEnf: (pending as any[]).length,
      });
    });
  }, [isStaff]);

  const handleLogout = () => { logout().finally(() => onLogout()); };

  const name = profile?.name?.trim();
  const prefix = profile?.iu_permit_prefix?.trim();

  return (
    <div style={s.page}>
      <header style={s.header}>
        <span style={s.brand}>Regreports PIMS</span>
        <span style={s.role}>{ROLE_LABEL[user.role] ?? user.role}</span>
        <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
      </header>

      <div style={s.content}>
        {/* Control Authority identity */}
        <div style={s.hero}>
          <div style={s.heroEyebrow}>Pretreatment Control Authority</div>
          <div style={s.heroName}>
            {name || <span style={{ color: "#a0aec0" }}>Control Authority name not set</span>}
          </div>
          <div style={s.heroMeta}>
            {prefix
              ? <>IU permit designation: <strong>{prefix}</strong></>
              : <span style={{ color: "#a0aec0" }}>No IU permit designation configured</span>}
            {profile?.address ? <span style={s.dot}>·</span> : null}
            {profile?.address}
          </div>
          {user.role === "admin" && (!name || !prefix) && (
            <div style={s.setupHint}>
              Set the agency name and IU permit designation in <strong>Admin → POTW Profile</strong>.
            </div>
          )}
        </div>

        {/* Program-at-a-glance (staff only) */}
        {isStaff && (
          <div style={s.cardGrid}>
            {[
              { label: "Industrial Users", value: stats?.ius },
              { label: "In SNC", value: stats?.inSnc, danger: (stats?.inSnc ?? 0) > 0 },
              { label: "Samples Due / Overdue", value: stats?.due, warn: (stats?.due ?? 0) > 0 },
              { label: "Pending Enforcement", value: stats?.pendingEnf, warn: (stats?.pendingEnf ?? 0) > 0 },
            ].map(c => (
              <div key={c.label} style={s.statCard}>
                <div style={{ ...s.statValue,
                  ...(c.danger ? { color: "#c53030" } : {}),
                  ...(c.warn ? { color: "#c05621" } : {}) }}>
                  {stats ? (c.value ?? 0) : "…"}
                </div>
                <div style={s.statLabel}>{c.label}</div>
              </div>
            ))}
          </div>
        )}

        <Link to="/dashboard" style={s.enterBtn}>
          Enter {ROLE_LABEL[user.role] ?? ""} Dashboard →
        </Link>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page:        { minHeight: "100vh", background: "#f0f4f8" },
  header:      { background: "#1a365d", color: "#fff", padding: "12px 24px",
                 display: "flex", alignItems: "center", gap: 16 },
  brand:       { fontSize: 20, fontWeight: 700, flex: 1 },
  role:        { fontSize: 13, background: "#2b6cb0", padding: "3px 10px", borderRadius: 12 },
  logoutBtn:   { background: "transparent", color: "#fff", border: "1px solid #fff",
                 borderRadius: 5, padding: "4px 12px", cursor: "pointer" },
  content:     { maxWidth: 920, margin: "0 auto", padding: "32px 24px" },
  hero:        { background: "#fff", borderRadius: 10, padding: "28px 32px",
                 boxShadow: "0 1px 6px rgba(0,0,0,0.08)", borderLeft: "5px solid #2b6cb0",
                 marginBottom: 20 },
  heroEyebrow: { fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                 color: "#2b6cb0" },
  heroName:    { fontSize: 28, fontWeight: 800, color: "#1a365d", margin: "6px 0 8px" },
  heroMeta:    { fontSize: 14, color: "#4a5568" },
  dot:         { margin: "0 8px", color: "#cbd5e0" },
  setupHint:   { marginTop: 14, fontSize: 13, color: "#975a16", background: "#fffaf0",
                 border: "1px solid #f6e05e", borderRadius: 6, padding: "8px 12px" },
  cardGrid:    { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                 gap: 14, marginBottom: 24 },
  statCard:    { background: "#fff", borderRadius: 10, padding: "18px 16px", textAlign: "center",
                 boxShadow: "0 1px 6px rgba(0,0,0,0.08)" },
  statValue:   { fontSize: 30, fontWeight: 800, color: "#1a365d" },
  statLabel:   { fontSize: 12, color: "#718096", marginTop: 4 },
  enterBtn:    { display: "inline-block", background: "#2b6cb0", color: "#fff",
                 padding: "12px 24px", borderRadius: 8, fontWeight: 700, fontSize: 15,
                 textDecoration: "none" },
};
